import { Link } from '@tanstack/react-router'
import { ArrowRight, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import MarketingLayout from './MarketingLayout'

export default function NotFoundPage() {
  const { t } = useTranslation()

  return (
    <MarketingLayout>
      <div className="min-h-screen font-body bg-[#060608] text-white antialiased overflow-x-hidden flex flex-col items-center justify-center selection:bg-purple-500/30 py-24 px-6 relative">
        
        {/* 404 Hero Section */}
        <section className="relative w-full max-w-[1280px] mx-auto flex flex-col items-center justify-center text-center flex-1 z-10 mb-20 mt-10">
          {/* Decorative 404 Background */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-5">
            <h1 className="text-[35vw] font-headline font-black leading-none text-white tracking-tighter mix-blend-overlay">
              404
            </h1>
          </div>
          
          {/* Glow effects */}
          <div
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)', filter: 'blur(50px)' }}
            aria-hidden
          />

          <div className="relative z-10 flex flex-col items-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7c3aed]/30 bg-[#7c3aed]/8 backdrop-blur-md mb-8 mt-12">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ec4899]" style={{ boxShadow: '0 0 8px rgba(236,72,153,0.8)' }} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70 font-headline">
                {t('notFound.badge', { defaultValue: 'Error 404' })}
              </span>
            </div>

            <h2 className="font-headline text-5xl sm:text-6xl md:text-[68px] font-bold leading-[1.08] tracking-[-0.025em] text-white mb-6 max-w-3xl mx-auto">
              {t('notFound.title', { defaultValue: 'Page not found' })}
            </h2>

            <p className="text-lg leading-relaxed text-white/60 max-w-xl mx-auto mb-10">
              {t('notFound.description', { defaultValue: "Oops! We can't seem to find the page you're looking for. It might have been moved or deleted." })}
            </p>

            <Link
              to="/"
              className="px-8 py-4 bg-[#7c3aed] text-white font-headline text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 rounded-xl transition-all hover:bg-[#6d28d9] hover:shadow-[0_0_32px_rgba(124,58,237,0.6)] active:scale-[0.98]"
            >
              {t('notFound.goHome', { defaultValue: 'Go to Home' })}
              <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="relative w-full max-w-3xl mx-auto z-10 mb-10">
          <div
            className="group relative rounded-[28px] border border-white/[0.06] bg-[#0c0c11] overflow-hidden p-8 sm:p-12 text-center transition-all duration-500 hover:border-white/[0.12] hover:bg-[#101016]"
            style={{ boxShadow: '0 10px 40px -10px rgba(0,0,0,0.5)' }}
          >
            {/* Ambient Glow */}
            <div
              className="pointer-events-none absolute top-[-50%] left-[-10%] w-[150%] h-[150%] rounded-full blur-[80px] opacity-40 transition-opacity duration-700 group-hover:opacity-60"
              style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 50%)' }}
              aria-hidden
            />
            {/* Grain */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay"
              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.8'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")" }}
              aria-hidden
            />

            <div className="relative z-10 flex flex-col items-center">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mb-6" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.05)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 24px -4px rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)' }}>
                <Mail className="w-6 h-6 text-[#d8b4fe]/80" />
              </div>
              
              <h3 className="font-headline text-2xl font-medium text-white mb-3">
                {t('notFound.newsletter.title', { defaultValue: 'Stay in the loop' })}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed mb-8 max-w-md mx-auto">
                {t('notFound.newsletter.description', { defaultValue: 'Subscribe to our newsletter to get the latest updates, programs, and news directly in your inbox.' })}
              </p>

              <form className="w-full max-w-md relative flex flex-col sm:flex-row gap-3" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  required
                  placeholder={t('notFound.newsletter.placeholder', { defaultValue: 'Enter your email address' })}
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-5 py-3.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#7c3aed]/50 focus:bg-white/[0.05] transition-all"
                />
                <button
                  type="submit"
                  className="px-6 py-3.5 bg-white text-[#100d16] font-headline text-sm font-bold uppercase tracking-wider rounded-xl transition-all hover:bg-white/90 active:scale-[0.98] whitespace-nowrap"
                >
                  {t('notFound.newsletter.button', { defaultValue: 'Subscribe' })}
                </button>
              </form>
            </div>
          </div>
        </section>

      </div>
    </MarketingLayout>
  )
}
